import { useState, useEffect, useCallback } from "react";
import { Button, Select, Tabs, Pagination } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import TicketTable from "./components/TicketTable";
import EmptyTickets from "./components/EmptyTickets";
import DeleteTicketsModal from "./components/DeleteTicketsModal";
import AddTicketModal from "./components/AddTicketModal";
import TicketPreviewPage from "./TicketPreview/TicketPreviewPage";
import TechnicalSupportChat from "./TechnicalSupport/TechnicalSupportChat";
import {
  getTickets,
  TicketResponse,
  TicketType,
  TicketStatus,
  PAGE_SIZE,
} from "./helpCenter.service";
import { getCurrentRole } from "../../utils/roles";
import "./HelpCenterPage.css";

type TechSubTab = "chat" | "my_tickets";

const HelpCenterPage = () => {
  const { t } = useTranslation("helpCenter");
  const isOwner = getCurrentRole() === "owner";
  const [activeTicketType, setActiveTicketType] =
    useState<TicketType>("team_support");
  const [techSubTab, setTechSubTab] = useState<TechSubTab>("chat");
  const [filterStatus, setFilterStatus] = useState<TicketStatus | undefined>(
    undefined,
  );
  const [currentPage, setCurrentPage] = useState(1);

  const [tickets, setTickets] = useState<TicketResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [typeCounts, setTypeCounts] = useState({
    team_support: 0,
    technical_support: 0,
  });
  const [loading, setLoading] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState<TicketResponse | null>(null);
  const [deleteTicketId, setDeleteTicketId] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);

  const fetchTickets = useCallback(async () => {
    if (activeTicketType === "technical_support" && techSubTab === "chat") return;
    setLoading(true);
    try {
      const res = await getTickets({
        ticket_type: activeTicketType,
        status: filterStatus,
        skip: (currentPage - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setTickets(res.tickets ?? []);
      setTotal(res.total ?? 0);
      setTypeCounts(
        res.counts_by_ticket_type ?? { team_support: 0, technical_support: 0 },
      );
    } catch {
      // handled by interceptor
    } finally {
      setLoading(false);
    }
  }, [activeTicketType, techSubTab, filterStatus, currentPage]);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const handleTabChange = (key: string) => {
    setActiveTicketType(key as TicketType);
    setTechSubTab("chat");
    setCurrentPage(1);
  };

  const handleFilterStatus = (val: TicketStatus | undefined) => {
    setFilterStatus(val);
    setCurrentPage(1);
  };

  if (selectedTicket) {
    return (
      <TicketPreviewPage
        ticket={selectedTicket}
        onBack={() => {
          setSelectedTicket(null);
          fetchTickets();
        }}
      />
    );
  }

  const showTicketTable =
    activeTicketType === "team_support" ||
    (activeTicketType === "technical_support" && techSubTab === "my_tickets");

  return (
    <div className="layout-style" style={{ marginLeft: 0 }}>
      <div className="tickets-card">
        <div className="tickets-card-header">
          <h2>{t("page.title")}</h2>
          {activeTicketType === "team_support" && (
            <Button
              className="add-ticket-button"
              icon={<PlusOutlined />}
              onClick={() => setAddModalOpen(true)}
            >
              {t("page.addTicket")}
            </Button>
          )}
        </div>

        <Tabs
          activeKey={activeTicketType}
          onChange={handleTabChange}
          items={[
            {
              key: "team_support",
              label: (
                <span>
                  {t("tabs.teamSupport")}{" "}
                  {typeCounts.team_support > 0 && (
                    <span className="tab-count">{typeCounts.team_support}</span>
                  )}
                </span>
              ),
            },
            ...(isOwner
              ? [
                  {
                    key: "technical_support",
                    label: (
                      <span>
                        {t("tabs.technicalSupport")}{" "}
                        {typeCounts.technical_support > 0 && (
                          <span className="tab-count">
                            {typeCounts.technical_support}
                          </span>
                        )}
                      </span>
                    ),
                  },
                ]
              : []),
          ]}
        />

        {isOwner && activeTicketType === "technical_support" && (
          <Tabs
            activeKey={techSubTab}
            onChange={(key) => {
              setTechSubTab(key as TechSubTab);
              setCurrentPage(1);
            }}
            className="tech-sub-tabs"
            items={[
              { key: "chat", label: t("tabs.chat") },
              {
                key: "my_tickets",
                label: (
                  <span>
                    {t("tabs.myTickets")}{" "}
                    {typeCounts.technical_support > 0 && (
                      <span className="tab-count">
                        {typeCounts.technical_support}
                      </span>
                    )}
                  </span>
                ),
              },
            ]}
          />
        )}

        {showTicketTable && (
          <div className="ticket-filters">
            <Select
              placeholder={t("filters.byStatus")}
              allowClear
              value={filterStatus}
              onChange={handleFilterStatus}
            >
              <Select.Option value="waiting_for_response">
                {t("status.waitingForResponse")}
              </Select.Option>
              <Select.Option value="in_progress">
                {t("status.inProgress")}
              </Select.Option>
              <Select.Option value="resolved">
                {t("status.resolved")}
              </Select.Option>
            </Select>
          </div>
        )}

        {isOwner && activeTicketType === "technical_support" && techSubTab === "chat" ? (
          <TechnicalSupportChat
            projectId=""
            onEscalated={(ticket) => setSelectedTicket(ticket)}
          />
        ) : (
          <>
            {!loading && tickets.length === 0 ? (
              <EmptyTickets />
            ) : (
              <TicketTable
                tickets={tickets}
                loading={loading}
                onOpenTicket={setSelectedTicket}
                onDeleteTicket={(t) => setDeleteTicketId(t.id)}
              />
            )}

            {total > PAGE_SIZE && (
              <div className="tickets-pagination">
                <Pagination
                  current={currentPage}
                  pageSize={PAGE_SIZE}
                  total={total}
                  onChange={setCurrentPage}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>

      <AddTicketModal
        open={addModalOpen}
        projectId=""
        onClose={() => setAddModalOpen(false)}
        onCreated={() => {
          setCurrentPage(1);
          fetchTickets();
        }}
      />

      <DeleteTicketsModal
        open={!!deleteTicketId}
        ticketId={deleteTicketId}
        onClose={() => setDeleteTicketId(null)}
        onDeleted={() => {
          setDeleteTicketId(null);
          fetchTickets();
        }}
      />
    </div>
  );
};

export default HelpCenterPage;
